import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, IconButton, List, ListItem, ListItemText, Paper, Tooltip, Typography } from '@mui/material';
import { ContentCopy, Download, OpenInNew } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import urljoin from 'url-join';
import { API_BASEPATH_PORTAL } from '@/config/env.config';
import { useCurrentUser } from '@/redux/services/portal/user.api';
import ManagementPageCard, { defaultInnerPanelSx } from '@/components/layout/ManagementPageCard.jsx';
import { UserPermissionTypes } from '@/security/permissionTypes';

const AgreementRow = ({ title, onDownload, onCopyLink, onOpenLink, downloadLabel, disabled, canShareLinks, isPermissionResolved }) => (
	<ListItem
		divider
		sx={{ py: 2 }}
		secondaryAction={(
			<Box sx={{ display: 'flex', gap: 1 }}>
				{canShareLinks && isPermissionResolved ? (
					<>
						<Tooltip title="Open Link">
							<IconButton size="small" onClick={onOpenLink} aria-label="Open Link">
								<OpenInNew fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="Copy Link">
							<IconButton size="small" onClick={onCopyLink} aria-label="Copy Link">
								<ContentCopy fontSize="small" />
							</IconButton>
						</Tooltip>
					</>
				) : !isPermissionResolved ? (
					// Reserve button space until permissions resolve to avoid layout flicker on first paint.
					<Box sx={{ display: 'flex', gap: 1, visibility: 'hidden' }}>
						<IconButton size="small" disabled aria-label="Loading open link permission">
							<OpenInNew fontSize="small" />
						</IconButton>
						<IconButton size="small" disabled aria-label="Loading copy link permission">
							<ContentCopy fontSize="small" />
						</IconButton>
					</Box>
				) : null}
				<Tooltip title={downloadLabel}>
					<span>
						<IconButton size="small" onClick={onDownload} disabled={disabled} aria-label={downloadLabel}>
							{disabled ? <CircularProgress size={16} /> : <Download fontSize="small" />}
						</IconButton>
					</span>
				</Tooltip>
			</Box>
		)}
	>
		<ListItemText
			primary={title}
			primaryTypographyProps={{ variant: 'body1' }}
		/>
	</ListItem>
);

export const LegalAgreements = () => {
	const { getAccessTokenSilently } = useAuth0();
	const { data: user, isLoading: isUserLoading } = useCurrentUser();
	const [isDownloadingEula, setIsDownloadingEula] = useState(false);
	const [isDownloadingFop, setIsDownloadingFop] = useState(false);
	const [downloadError, setDownloadError] = useState('');
	const [linkMessage, setLinkMessage] = useState('');
	const canShareLinks = useMemo(() => {
		if (!user?.roles) return false;
		return user.roles.some((role) =>
			Array.isArray(role?.permissions) && role.permissions.includes(UserPermissionTypes.canViewAdmin)
		);
	}, [user]);
	const isPermissionResolved = !isUserLoading;

	const openDocumentLink = useCallback((path) => {
		const fullUrl = `${window.location.origin}${path}`;
		window.open(fullUrl, '_blank', 'noopener,noreferrer');
	}, []);

	const copyDocumentLink = useCallback(async (path) => {
		try {
			const fullUrl = `${window.location.origin}${path}`;
			await navigator.clipboard.writeText(fullUrl);
			setLinkMessage('Link copied to clipboard.');
		} catch (error) {
			setLinkMessage('Failed to copy link. Please copy from the browser address bar.');
		}
	}, []);

	const downloadEula = useCallback(async () => {
		try {
			setDownloadError('');
			setIsDownloadingEula(true);
			const url = urljoin(API_BASEPATH_PORTAL, 'eula', 'download');
			const token = await getAccessTokenSilently({ audience: 'https://portal-api' });
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${token}`,
					'X-Content-Type-Options': 'nosniff',
					'X-XSS-Protection': 1,
				},
			});

			if (!response.ok) {
				throw new Error(`Failed to download EULA: ${response.statusText}`);
			}

			let filename = 'EULA.pdf';
			const contentDisposition = response.headers.get('Content-Disposition');
			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
				if (filenameMatch && filenameMatch[1]) {
					filename = filenameMatch[1].replace(/['"]/g, '');
				}
			}

			const blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = blobUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(blobUrl);
		} catch (error) {
			console.error('Failed to download EULA:', error);
			setDownloadError('Failed to download EULA file. Please try again.');
		} finally {
			setIsDownloadingEula(false);
		}
	}, [getAccessTokenSilently]);

	const downloadFop = useCallback(async () => {
		try {
			setDownloadError('');
			setIsDownloadingFop(true);
			const url = urljoin(API_BASEPATH_PORTAL, 'shared-files', 'FairUsePolicy', 'download');
			const token = await getAccessTokenSilently({ audience: 'https://portal-api' });
			const response = await fetch(url, {
				method: 'GET',
				headers: {
					Authorization: `Bearer ${token}`,
					'X-Content-Type-Options': 'nosniff',
					'X-XSS-Protection': 1,
				},
			});

			if (!response.ok) {
				throw new Error(`Failed to download Fair Use Policy: ${response.statusText}`);
			}

			let filename = 'Fair-Use-Policy';
			const contentDisposition = response.headers.get('Content-Disposition');
			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
				if (filenameMatch && filenameMatch[1]) {
					filename = filenameMatch[1].replace(/['"]/g, '');
				}
			}

			const blob = await response.blob();
			const blobUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = blobUrl;
			link.download = filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(blobUrl);
		} catch (error) {
			console.error('Failed to download Fair Use Policy:', error);
			setDownloadError('Failed to download Fair Use Policy file. Please try again.');
		} finally {
			setIsDownloadingFop(false);
		}
	}, [getAccessTokenSilently]);

	return (
		<ManagementPageCard
			title="Legal Agreements"
			subtitle="Download legal documents associated with your Portal access."
		>
			{user && !user.eulaAccepted && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					Your account has not completed EULA acceptance yet.
				</Alert>
			)}
			{downloadError && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{downloadError}
				</Alert>
			)}
			{linkMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{linkMessage}
				</Alert>
			)}

			<Box sx={defaultInnerPanelSx}>
				<Paper variant="outlined" sx={{ width: '100%' }}>
					<List disablePadding>
						<AgreementRow
							title="End User License Agreement (EULA)"
							onDownload={downloadEula}
							onCopyLink={() => copyDocumentLink('/legal/eula')}
							onOpenLink={() => openDocumentLink('/legal/eula')}
							downloadLabel={isDownloadingEula ? 'Downloading...' : 'Download'}
							disabled={isDownloadingEula}
							canShareLinks={canShareLinks}
							isPermissionResolved={isPermissionResolved}
						/>
						<AgreementRow
							title="Fair Use Policy"
							onDownload={downloadFop}
							onCopyLink={() => copyDocumentLink('/legal/fair-use-policy')}
							onOpenLink={() => openDocumentLink('/legal/fair-use-policy')}
							downloadLabel={isDownloadingFop ? 'Downloading...' : 'Download'}
							disabled={isDownloadingFop}
							canShareLinks={canShareLinks}
							isPermissionResolved={isPermissionResolved}
						/>
					</List>
				</Paper>
			</Box>
		</ManagementPageCard>
	);
};

export default LegalAgreements;
